import pageTitle from 'ember-page-title/helpers/page-title';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import type PreRegisterService from '../services/pre-register';
import type RouterService from '@ember/routing/router';
import type SessionService from '../services/session';
import type Owner from '@ember/owner';
import { t } from 'ember-intl';
import TioFooter from 'tio-common/components/tio/footer';
import { navigation } from 'tio-common/utils/navigation';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import { PhQuestion } from 'ember-phosphor-icons';
import TioWordmark from 'tio-common/components/beta/tio-wordmark';
import {
  REMEMBER_ATTRIBUTE,
  forgetDestination,
  markRedirected,
  redirectedThisTab,
  rememberDestination,
  rememberedDestination,
  welcomeRedirect,
} from '../utils/welcome-destination';

export default class WelcomeRoute extends Component {
  @service declare preRegister: PreRegisterService;
  @service declare router: RouterService;
  @service declare session: SessionService;

  @tracked choosingAgain: boolean;
  @tracked failedLogos = 0;

  constructor(owner: Owner, args: object) {
    super(owner, args);
    this.choosingAgain = redirectedThisTab(this.preRegister.subdomain);
  }

  get settings() {
    return this.preRegister.settings?.landingPage;
  }

  get title() {
    return this.settings?.title;
  }

  get content() {
    return this.settings?.html;
  }

  get companyName() {
    return this.preRegister.companyName ?? '';
  }

  get logoUrl() {
    const candidates = [this.preRegister.uploadedLogoUrl, this.preRegister.logo].filter(Boolean);
    return candidates[this.failedLogos];
  }

  // Try the next candidate; past the last one the logo slot renders empty.
  nextLogo = () => {
    this.failedLogos += 1;
  };

  get buttonLabel() {
    return this.settings?.buttonText?.trim() || undefined;
  }

  get buttonUrl() {
    return this.settings?.externalLoginUrl;
  }

  get remembersDestination() {
    return this.settings?.rememberDestination === true;
  }

  @cached
  get pendingDestination() {
    if (this.choosingAgain || !this.remembersDestination) return undefined;
    return rememberedDestination(this.preRegister.subdomain, this.content);
  }

  gotoLogin = async () => {
    this.session?.setTransientValue?.('landingPageViewed', true);
    this.router.transitionTo('login');
  };

  #focusChoices = false;

  chooseAgain = () => {
    this.followDestination.cancelAll();
    forgetDestination(this.preRegister.subdomain);
    this.#focusChoices = true;
    this.choosingAgain = true;
  };

  followDestination = restartableTask(async (href: string) => {
    await timeout(welcomeRedirect.delayMs);
    markRedirected(this.preRegister.subdomain);
    navigation.navigateTo(href);
  });

  scheduleRedirect = modifier((_element: HTMLElement, [href]: [string]) => {
    // A page restored from the back/forward cache would otherwise sit on "Taking you to…".
    const showChoices = (event: PageTransitionEvent) => {
      if (event.persisted) this.choosingAgain = true;
    };
    window.addEventListener('pageshow', showChoices);
    void this.followDestination.perform(href);
    return () => {
      window.removeEventListener('pageshow', showChoices);
      this.followDestination.cancelAll();
    };
  });

  // Delegated listener: the links live in admin-authored HTML, not in this template.
  rememberChoice = modifier((element: HTMLElement) => {
    if (this.#focusChoices) {
      this.#focusChoices = false;
      const target =
        element.querySelector<HTMLElement>(`a[${REMEMBER_ATTRIBUTE}]`) ??
        element.querySelector<HTMLElement>('a[href]');
      target?.focus();
    }

    const remember = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.(`a[${REMEMBER_ATTRIBUTE}]`);
      const href = anchor?.getAttribute('href');
      if (this.remembersDestination && anchor && href && element.contains(anchor)) {
        rememberDestination(this.preRegister.subdomain, href);
      }
    };
    element.addEventListener('click', remember);
    return () => element.removeEventListener('click', remember);
  });

  <template>
    {{pageTitle (t "login.page_title")}}

    <div class="tio-col-flush min-h-screen tio-bg-sunken">
      <header class="tio-row justify-between items-center px-4 sm:px-6 py-6">
        <TioWordmark class="tio-fg-brand h-6 w-auto shrink-0" />
        <p class="tio-row gap-2 items-center whitespace-nowrap tio-text-sm-normal tio-fg-secondary">
          <PhQuestion @size={{18}} aria-hidden="true" />
          <span class="hidden sm:inline">{{t "login.need_help"}}</span>
          <a
            href="https://www.tuition.io/support"
            target="_blank"
            rel="noopener noreferrer"
            class="underline tio-fg-info"
          >
            {{t "login.contact_support"}}
          </a>
        </p>
      </header>

      <main class="px-4 pb-16">
        <Card @padding="lg" class="mx-auto w-full max-w-xl" data-test-welcome-card>
          {{#if this.logoUrl}}
            <img
              src={{this.logoUrl}}
              alt={{this.companyName}}
              class="h-14 w-auto max-w-full object-contain mb-6"
              data-test-welcome-logo
              {{on "error" this.nextLogo}}
            />
          {{/if}}

          {{#if this.title}}
            <h1 class="mb-4">{{this.title}}</h1>
          {{/if}}

          {{#if this.pendingDestination}}
            <div
              class="tio-col tio-text-base-normal tio-fg-secondary"
              data-test-welcome-redirect
              {{this.scheduleRedirect this.pendingDestination.href}}
            >
              <p role="status">
                {{t "welcome.redirecting" destination=this.pendingDestination.label}}
              </p>
              <Button
                @appearance="link"
                @intent="primary"
                @testHook="welcome__choose-again"
                data-test-welcome-choose-again
                {{on "click" this.chooseAgain}}
              >
                {{t "welcome.choose_different"}}
              </Button>
            </div>
          {{else}}
            <div class="welcome-html tio-text-base-normal tio-fg-secondary" {{this.rememberChoice}}>
              {{! template-lint-disable no-triple-curlies }}
              {{{this.content}}}
            </div>

            {{#if this.buttonLabel}}
              <div class="mt-6">
                {{#if this.buttonUrl}}
                  <Button
                    @href={{this.buttonUrl}}
                    @appearance="outlined"
                    @isFullWidth={{true}}
                    @testHook="welcome__login"
                  >
                    {{this.buttonLabel}}
                  </Button>
                {{else}}
                  <Button
                    @appearance="outlined"
                    @isFullWidth={{true}}
                    @testHook="welcome__login"
                    {{on "click" this.gotoLogin}}
                  >
                    {{this.buttonLabel}}
                  </Button>
                {{/if}}
              </div>
            {{/if}}
          {{/if}}
        </Card>
      </main>

      <div class="mt-auto pb-20 mx-6 lg:mx-12">
        <TioFooter />
      </div>
    </div>
  </template>
}
